import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Search, Loader2 } from 'lucide-react';
import { StatusPillType } from '@/hooks/useGlobalStatus';
import { handleAppError } from '@/lib/errorUtils';
import { fetchJobDescriptionFromUrl } from '@/services/jobFetcher';

interface JobDescriptionInputProps {
  jobText: string;
  setJobText: (text: string) => void;
  jobLink: string;
  setJobLink: (link: string) => void;
  setTargetDetails: React.Dispatch<React.SetStateAction<any>>;
  setStatus: (status: StatusPillType) => void;
  setStatusMessage: (msg: string) => void;
  isFetchingLink: boolean;
  setIsFetchingLink: (val: boolean) => void;
  extractMetadataForText: (text: string) => Promise<void>;
  isExtracting: boolean;
  setSessionCost?: React.Dispatch<React.SetStateAction<number>>;
  setSessionTokens?: React.Dispatch<React.SetStateAction<{in: number, out: number}>>;
}

export const JobDescriptionInput = React.memo(function JobDescriptionInput({ 
  jobText, setJobText, 
  jobLink, setJobLink, 
  setTargetDetails, 
  setStatus, setStatusMessage,
  isFetchingLink, setIsFetchingLink,
  extractMetadataForText, isExtracting,
  setSessionCost, setSessionTokens,
  onRequireAuth
}: JobDescriptionInputProps & { onRequireAuth?: () => boolean }) {

  const [linkError, setLinkError] = useState('');

  const handleSearchOrExtract = async () => {
    let finalJobText = jobText;
    const linkToFetch = jobLink.trim();
    setLinkError('');

    if (linkToFetch) {
      if (onRequireAuth && !onRequireAuth()) {
        return; // require auth only for link fetching
      }
      if (!/^https?:\/\//i.test(linkToFetch)) {
        setLinkError('Please enter a valid URL starting with http:// or https://');
        return;
      }

      setIsFetchingLink(true);
      setStatus('working');
      setStatusMessage('Fetching job description...');
      try {
        const { searchJobDescriptionWithAI, cleanJobDescriptionWithAI } = await import('@/services/geminiService');
        const res = await fetchJobDescriptionFromUrl(linkToFetch, searchJobDescriptionWithAI, cleanJobDescriptionWithAI);
        
        if (res.cost > 0 && setSessionCost) {
            setSessionCost(prev => prev + res.cost);
        }
        if (res.promptTokens > 0 && setSessionTokens) {
            setSessionTokens(prev => ({ in: prev.in + res.promptTokens, out: prev.out + res.completionTokens }));
        }

        finalJobText = res.text.substring(0, 15000);
        setJobText(finalJobText);
        setTargetDetails((prev: any) => ({ ...prev, company: '', position: '' }));
      } catch (err: any) {
        setStatus('error');
        setStatusMessage(handleAppError(err, 'Could not fetch the job link automatically.'));
        setIsFetchingLink(false);
        return; // Stop here if fetching failed
      } finally {
        setIsFetchingLink(false); // Link fetch is done
      }
    }

    // Now extract metadata based on the job text (fetched or manually written)
    if (finalJobText && finalJobText.trim().length >= 50) {
      setStatus('working');
      setStatusMessage(jobLink ? 'Job description fetched. Extracting details...' : 'Extracting details from text...');
      try {
        await extractMetadataForText(finalJobText);
        setStatus('ready');
        setStatusMessage(jobLink ? 'Details extracted from link' : 'Details extracted from text');
      } catch (err: any) {
        setStatus('error');
        setStatusMessage(handleAppError(err, 'Failed to extract useful metadata. Please verify your job description.'));
      }
    } else {
      if (!jobLink) {
         setStatus('error');
         setStatusMessage('Please enter a job link or sufficient job description text.');
      }
    }
  };

  return (
    <motion.section initial={{opacity: 0, y: 10}} animate={{opacity: 1, y: 0}} transition={{duration: 0.3}}>
      <h2 className="text-xs font-semibold text-slate-700 dark:text-slate-400 tracking-wider uppercase mb-3 px-1">
        <label htmlFor="jobDescription" className="flex justify-between items-center w-full">
          <span>2. Job Description or Link</span>
        </label>
      </h2>
      <div className="mb-3 relative">
        <input
          type="url"
          aria-label="Job link URL"
          className={`w-full text-sm text-slate-700 dark:text-slate-300 border ${linkError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-200 dark:border-slate-800 focus:ring-emerald-500 focus:border-slate-200 dark:focus:border-slate-700'} rounded-lg px-3 py-2 bg-white dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-900 focus:bg-slate-100 dark:focus:bg-slate-900 dark:shadow-inner focus:outline-none focus:ring-1 placeholder:text-slate-500 dark:placeholder:text-slate-500`}
          placeholder="Paste link to fetch description..."
          value={jobLink}
          onChange={(e) => {
            setJobLink(e.target.value);
            if (linkError) setLinkError('');
          }}
          onKeyDown={(e) => e.key === 'Enter' && handleSearchOrExtract()}
        />
        {linkError && <p className="text-[11px] text-red-500 mt-1">{linkError}</p>}
      </div>
      <textarea
        id="jobDescription"
        className="w-full text-sm text-slate-700 dark:text-slate-300 leading-relaxed border border-slate-200 dark:border-slate-800 rounded-xl p-4 min-h-[170px] resize-y bg-white dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-900 focus:bg-slate-100 dark:focus:bg-slate-900 dark:shadow-inner focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-slate-200 dark:focus:border-slate-700 placeholder:text-slate-500 dark:placeholder:text-slate-500"
        placeholder="Or paste the job requirements, responsibilities, and description manually..."
        value={jobText} onChange={(e) => setJobText(e.target.value)} spellCheck="false"
      />
      <div className="mt-3">
        <button
          onClick={handleSearchOrExtract}
          disabled={(!jobLink && !jobText) || isFetchingLink || isExtracting}
          className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 dark:bg-emerald-600 dark:hover:bg-emerald-500 text-white font-medium rounded-lg px-4 py-2 disabled:opacity-50 transition-colors dark:shadow-sm cursor-pointer disabled:cursor-not-allowed"
          title="Fetch link and/or extract details"
        >
          {isFetchingLink || isExtracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          <span className="text-sm">Extract Details</span>
        </button>
      </div>
    </motion.section>
  );
});
